import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';
import { getEntityStrategy, LOAD_STRATEGIES, REFERENCE_ENTITIES } from '@/config/reference.config.js';
import { useTenantStore } from '@/store/tenant.store.js';
import {
    isPersistedTenantMatch,
    mergeReferencePersistState,
} from '@/lib/services/reference-tenant-cache.js';

export { REFERENCE_ENTITIES };
export { isPersistedTenantMatch, mergeReferencePersistState } from '@/lib/services/reference-tenant-cache.js';

function currentTenantId() {
    return useTenantStore.getState().selectedTenantId || null;
}

const LOOKUP_RESOLVE_CAP = 5000;

function trimResolveLookupMap(entity, merged) {
    const s = getEntityStrategy(entity);
    const shouldTrim = s === LOAD_STRATEGIES.REMOTE_SEARCH
        || s === LOAD_STRATEGIES.RESOLVE_ONLY
        || s === LOAD_STRATEGIES.HYBRID;
    if (!shouldTrim) return merged;
    while (merged.size > LOOKUP_RESOLVE_CAP) {
        const k = merged.keys().next().value;
        merged.delete(k);
    }
    return merged;
}

const idbStorage = {
    getItem: async (name) => {
        const val = await idbGet(name);
        return val ?? null;
    },
    setItem: async (name, value) => {
        await idbSet(name, value);
    },
    removeItem: async (name) => {
        await idbDel(name);
    },
};

function buildEmptyEntities() {
    const obj = {};
    REFERENCE_ENTITIES.forEach(e => { obj[e] = []; });
    return obj;
}

// Create stable empty arrays for each entity type to prevent infinite re-renders
const STABLE_EMPTY_ARRAYS = {};
REFERENCE_ENTITIES.forEach(e => { STABLE_EMPTY_ARRAYS[e] = []; });

function buildLookupMaps(entities, seedData = {}) {
    const maps = {};
    const keys = new Set([
        ...Object.keys(entities || {}),
        ...Object.keys(seedData || {}),
    ]);
    for (const key of keys) {
        const map = new Map();
        const mergeItems = (items) => {
            if (!Array.isArray(items)) return;
            items.forEach((item) => {
                const id = item.value?._id?.toString?.() || item.value?.toString?.() || item._id?.toString?.();
                if (id) map.set(id, item);
            });
        };
        // Seed first, then entities so full bulk rows win over capped seed.
        mergeItems(seedData?.[key]);
        mergeItems(entities?.[key]);
        maps[key] = map;
    }
    return maps;
}

export const useReferenceStore = create(
    persist(
        (set, get) => ({
            entities: buildEmptyEntities(),

            versions: {},

            /** Tenant that owns the persisted entities/versions (null = empty / unknown). */
            persistedTenantId: null,

            lastHydratedAt: null,
            isHydrating: false,
            hydratedEntities: {},

            _lookups: {},

            seedData: {},

            searchResults: {},

            mergeResolvedItems: (entity, items) => {
                if (!Array.isArray(items) || items.length === 0) return;
                set((state) => {
                    const newLookups = { ...state._lookups };
                    const existing = newLookups[entity] || new Map();
                    const merged = new Map(existing);
                    items.forEach((item) => {
                        const id = item.value?._id?.toString?.() || item.value?.toString?.() || item._id?.toString?.();
                        if (id) merged.set(id, item);
                    });
                    newLookups[entity] = trimResolveLookupMap(entity, merged);
                    return { _lookups: newLookups };
                });
            },

            setSeedData: (entity, items, version) => {
                const tenantId = currentTenantId();
                set((state) => {
                    const newSeed = { ...state.seedData, [entity]: items };
                    const newVersions = version != null
                        ? { ...state.versions, [entity]: version }
                        : state.versions;
                    const newLookups = { ...state._lookups };
                    const existing = newLookups[entity] || new Map();
                    const merged = new Map(existing);
                    if (Array.isArray(items)) {
                        items.forEach((item) => {
                            const id = item.value?._id?.toString?.() || item.value?.toString?.() || item._id?.toString?.();
                            if (id) merged.set(id, item);
                        });
                    }
                    newLookups[entity] = trimResolveLookupMap(entity, merged);
                    return {
                        seedData: newSeed,
                        versions: newVersions,
                        _lookups: newLookups,
                        persistedTenantId: tenantId ?? state.persistedTenantId,
                    };
                });
            },

            setSearchResults: (entity, items) => {
                set((state) => ({
                    searchResults: { ...state.searchResults, [entity]: items },
                }));
            },

            /**
             * Bump version only (paginated list sync for resolveOnly / remoteSearch entities).
             * @param {string} entity
             * @param {number} [version] — when omitted, increments local counter
             */
            bumpEntityVersion: (entity, version) => {
                set((state) => {
                    const nextVersion = version != null
                        ? version
                        : (state.versions[entity] || 0) + 1;
                    return {
                        versions: { ...state.versions, [entity]: nextVersion },
                    };
                });
            },

            setEntityData: (entity, items, version) => {
                const tenantId = currentTenantId();
                set((state) => {
                    const newEntities = { ...state.entities, [entity]: items };
                    const newVersions = { ...state.versions, [entity]: version };
                    const newHydrated = { ...state.hydratedEntities, [entity]: Date.now() };
                    const newLookups = { ...state._lookups };
                    const map = new Map();
                    items.forEach(item => {
                        const id = item.value?._id?.toString?.() || item.value?.toString?.() || item._id?.toString?.();
                        if (id) map.set(id, item);
                    });
                    newLookups[entity] = map;
                    return {
                        entities: newEntities,
                        versions: newVersions,
                        hydratedEntities: newHydrated,
                        _lookups: newLookups,
                        persistedTenantId: tenantId ?? state.persistedTenantId,
                    };
                });
            },

            setMultipleEntities: (dataMap, versionsMap) => {
                const tenantId = currentTenantId();
                set((state) => {
                    const newEntities = { ...state.entities };
                    const newVersions = { ...state.versions, ...versionsMap };
                    const newHydrated = { ...state.hydratedEntities };
                    const newLookups = { ...state._lookups };
                    const now = Date.now();

                    for (const [entity, payload] of Object.entries(dataMap)) {
                        if (!payload) continue;
                        const items = payload.items || payload;
                        newEntities[entity] = items;
                        newHydrated[entity] = now;

                        const map = new Map();
                        if (Array.isArray(items)) {
                            items.forEach(item => {
                                const id = item.value?._id?.toString?.() || item.value?.toString?.() || item._id?.toString?.();
                                if (id) map.set(id, item);
                            });
                        }
                        newLookups[entity] = map;
                    }

                    return {
                        entities: newEntities,
                        versions: newVersions,
                        hydratedEntities: newHydrated,
                        lastHydratedAt: now,
                        _lookups: newLookups,
                        persistedTenantId: tenantId ?? state.persistedTenantId,
                    };
                });
            },

            setHydrating: (val) => set({ isHydrating: val }),

            getLabel: (entity, id) => {
                const lookups = get()._lookups;
                const map = lookups[entity];
                if (!map) return null;
                const item = map.get(id?.toString?.() || id);
                return item?.label || null;
            },

            getItem: (entity, id) => {
                const lookups = get()._lookups;
                const map = lookups[entity];
                if (!map) return null;
                return map.get(id?.toString?.() || id) || null;
            },

            rebuildLookups: () => {
                set((state) => ({
                    _lookups: buildLookupMaps(state.entities, state.seedData),
                }));
            },

            reset: () => {
                set({
                    entities: buildEmptyEntities(),
                    versions: {},
                    persistedTenantId: null,
                    lastHydratedAt: null,
                    isHydrating: false,
                    hydratedEntities: {},
                    _lookups: {},
                    seedData: {},
                    searchResults: {},
                });
            },
        }),
        {
            name: 'sgrc-reference-data',
            // v3: documentType projection moved to documentCategory; drop stale id-as-label rows.
            version: 3,
            storage: idbStorage,
            migrate: (persistedState, fromVersion) => {
                if (!persistedState || !persistedState.persistedTenantId || fromVersion < 3) {
                    return {
                        entities: buildEmptyEntities(),
                        versions: {},
                        hydratedEntities: {},
                        seedData: {},
                        lastHydratedAt: null,
                        persistedTenantId: null,
                    };
                }
                return persistedState;
            },
            merge: (persistedState, currentState) => mergeReferencePersistState(
                persistedState,
                currentState,
                currentTenantId(),
                buildEmptyEntities,
            ),
            partialize: (state) => ({
                entities: state.entities,
                versions: state.versions,
                lastHydratedAt: state.lastHydratedAt,
                hydratedEntities: state.hydratedEntities,
                seedData: state.seedData,
                persistedTenantId: state.persistedTenantId,
            }),
            onRehydrateStorage: () => (state) => {
                if (!state) return;
                const selected = currentTenantId();
                if (!isPersistedTenantMatch(state.persistedTenantId, selected)) {
                    state.reset();
                    return;
                }
                state.rebuildLookups();
            },
        }
    )
);

export const selectUnits = (s) => s.entities.unit ?? STABLE_EMPTY_ARRAYS.unit;
export const selectRoles = (s) => s.entities.role ?? STABLE_EMPTY_ARRAYS.role;
export const selectUsers = (s) => {
    const full = s.entities.user;
    if (full && full.length > 0) return full;
    return s.seedData?.user ?? STABLE_EMPTY_ARRAYS.user;
};
export const selectCompanies = (s) => s.entities.company ?? STABLE_EMPTY_ARRAYS.company;
export const selectTeams = (s) => s.entities.team ?? STABLE_EMPTY_ARRAYS.team;
export const selectProcesses = (s) => s.entities.process ?? STABLE_EMPTY_ARRAYS.process;
export const selectAssetCategories = (s) => s.entities.assetCategory ?? STABLE_EMPTY_ARRAYS.assetCategory;
export const selectAssetGroups = (s) => s.entities.assetGroup ?? STABLE_EMPTY_ARRAYS.assetGroup;
export const selectDocumentTypes = (s) => s.entities.documentType ?? STABLE_EMPTY_ARRAYS.documentType;
export const selectClassifications = (s) => s.entities.classification ?? STABLE_EMPTY_ARRAYS.classification;
export const selectTicketCategories = (s) => s.entities.ticketCategory ?? STABLE_EMPTY_ARRAYS.ticketCategory;
export const selectTicketSlas = (s) => s.entities.ticketSLA ?? STABLE_EMPTY_ARRAYS.ticketSLA;
export const selectThreats = (s) => s.entities.threat ?? STABLE_EMPTY_ARRAYS.threat;
export const selectWeaknesses = (s) => s.entities.weakness ?? STABLE_EMPTY_ARRAYS.weakness;
export const selectVendors = (s) => s.entities.vendor ?? STABLE_EMPTY_ARRAYS.vendor;
export const selectVersions = (s) => s.versions;
export const selectIsHydrating = (s) => s.isHydrating;
